import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";

import { FaChevronRight } from "react-icons/fa";
import { cn } from "@/utils/cn";
import { handleUpdatedRoutes } from "@/utils/helper";
import { sidebarData } from "@/data/app-data";
import { useGet } from "@/apiHooks/HttpFunctions";
import { countTicketsApi } from "@/apis/apis";

const filterSubmenu = (node, permissionList) => {
  if (
    node.permissionText === true ||
    permissionList.includes(node.permissionText)
  ) {
    return true;
  }
  if (!Array.isArray(node.submenu) || node.submenu.length === 0) {
    return false;
  }

  node.submenu = node.submenu.filter((menu) =>
    filterSubmenu(menu, permissionList)
  );

  return node.submenu.length > 0;
};

const SidebarRecursive = ({
  items,
  sidebarOpen,
  openSubmenu,
  setOpenSubmenu,
  counters,
  level = 0,
}) => {
  const location = useLocation();
  const toggleSubMenu = (item, level) => {
    setOpenSubmenu((prev) => {
      const newOpen = { ...prev };
      if (level === 0) {
        if (newOpen[0] === item) {
          return {};
        } else {
          return { 0: item };
        }
      } else {
        if (newOpen[level] === item) {
          delete newOpen[level];
        } else {
          newOpen[level] = item;
        }
      }

      return newOpen;
    });
  };

  return (
    <>
      {items.map((item, index) => {
        const isActive = location.pathname === item?.pagePath;
        const isOpen = openSubmenu[level] === item;

        // add badge counter
        const badgeCount = item.badgeKey ? counters[item.badgeKey] || 0 : 0;

        return (
          <div key={index} className={cn("w-full")}>
            <Link
              to={item?.pagePath}
              onClick={(e) => {
                if (level === 0 && !isOpen) {
                  setOpenSubmenu({});
                }

                if (item?.submenu) {
                  toggleSubMenu(item, level);
                  e.preventDefault();
                }
              }}
              className={cn(
                "h-8 relative text-jersium-black flex items-center w-full gap-x-2 cursor-pointer",
                isActive ? "angular-gradient text-white" : "hover:bg-gray-100",
                !sidebarOpen && "justify-center"
              )}
              style={{
                paddingLeft: sidebarOpen ? `${16 + level * 16}px` : 0,
                paddingRight: sidebarOpen ? 16 : 0,
              }}
            >
              <div className="size-6 rounded flex items-center justify-center">
                <img
                  src={item.svg}
                  alt="icon"
                  className={cn(
                    "size-3.5",
                    isActive && "filter brightness-0 invert"
                  )}
                />
              </div>

              <span
                className={`font-light flex-1 flex justify-between items-center text-[14px] leading-[15px] transition-all duration-300 ${
                  sidebarOpen ? "block" : "hidden"
                }`}
              >
                <span className="flex items-center">
                  {item.name}
                  {/* Badge */}
                  {item.showBadge && badgeCount > 0 && (
                    <span className="bg-red-500 text-white text-[10px] ml-1 mb-1 rounded-full size-4 flex items-center justify-center">
                      {badgeCount}
                    </span>
                  )}
                </span>
              </span>

              {item?.submenu && (
                <div className="flex items-center justify-end">
                  <button className="p-1">
                    <FaChevronRight
                      className={cn(isOpen ? "rotate-90" : "ps-[1px]")}
                      size={10}
                    />
                  </button>
                </div>
              )}
            </Link>
            {item.submenu && isOpen && (
              <SidebarRecursive
                items={item.submenu}
                sidebarOpen={sidebarOpen}
                openSubmenu={openSubmenu}
                setOpenSubmenu={setOpenSubmenu}
                counters={counters}
                level={level + 1}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

export default function SidebarItems({ sidebarOpen }) {
  const dispatch = useDispatch();
  const { accessToken, allPermissions, user } = useSelector(
    (state) => state?.authReducer
  );

  // GET COUNTER DATA
  const {
    data: countersData,
    // isFetching: showIsFetching,
  } = useGet({
    apiURL: countTicketsApi,
    enabled: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: true,
  });

  const [updatedSidebarData, setUpdatedSidebarData] = useState([]);
  const [openSubmenu, setOpenSubmenu] = useState({});

  useEffect(() => {
    let updatedSidebar = structuredClone(sidebarData).filter((item) => {
      if (item?.permissionText) {
        let isPermission = allPermissions.includes(item.permissionText);
        return isPermission || item?.permissionText === true;
      } else if (item?.submenu) {
        return filterSubmenu(item, allPermissions);
      } else {
        return false;
      }
    });
    setUpdatedSidebarData(structuredClone(updatedSidebar));
    handleUpdatedRoutes({
      sidebarData: structuredClone(updatedSidebar),
      allPermissions,
      dispatch,
    });
  }, [allPermissions, user, accessToken]);

  return (
    <div className="flex-1 overflow-y-scroll w-full flex flex-col">
      <SidebarRecursive
        items={updatedSidebarData}
        sidebarOpen={sidebarOpen}
        openSubmenu={openSubmenu}
        setOpenSubmenu={setOpenSubmenu}
        counters={countersData?.data || 0}
      />
    </div>
  );
}
